/**
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import React from 'react';

import OlLayer from 'ol/layer/Layer';

import { ArrayTwoOrMore } from '@terrestris/base-util/dist/types';
import useMap from '@terrestris/react-geo/dist/Hook/useMap';

import BasicMapComponent from './components/BasicMapComponent/BasicMapComponent';
import BasicNominatimSearch from './components/BasicNominatimSearch/BasicNominatimSearch';
import Footer from './components/Footer/Footer';
import ProjectGeoLocationButton from './components/ProjectGeoLocationButton/ProjectGeoLocationButton';
import ProjectLayerSwitcher from './components/ProjectLayerSwitcher/ProjectLayerSwitcher';
import ProjectPrintButton from './components/ProjectPrintButton/ProjectPrintButton';
import ProjectZoomToExtentButton from './components/ProjectZoomToExtentButton/ProjectZoomToExtentButton';
import SideDrawer from './components/SideDrawer/SideDrawer';
import ToggleDrawerButton from './components/ToggleDrawerButton/ToggleDrawerButton';
import ToggleFeatureInfoDrawerButton from './components/ToggleFeatureInfoButton';

import './App.less';

export type AppProps = {
  backgroundLayers?: ArrayTwoOrMore<OlLayer>; // preparation for future layer chooser
};

export const App: React.FC<AppProps> = ({
  backgroundLayers
}): JSX.Element => {

  const map = useMap();
  if (!map) {
    return <></>;
  }

  return (
    <div className="App">
      <BasicMapComponent />
      <BasicNominatimSearch />
      <ToggleFeatureInfoDrawerButton />
      <SideDrawer />
      {
        backgroundLayers && (
          <ProjectLayerSwitcher
            layers={backgroundLayers}
            map={map}
          />
        )
      }
      <ProjectGeoLocationButton />
      <ProjectZoomToExtentButton />
      <ToggleDrawerButton />
      <ProjectPrintButton />
      <Footer />
    </div>
  );
};

export default App;
